import {
	linkWorkOrderAttachments,
	unLinkWorkOrderAttachments,
} from "@/pages/work-orders/repositorie/WorkOrdersRepositorie";
import { useWorkOrderModalsContext } from "@/shared/work-orders-modals/context/work-order-modals.context";
import type { GridApi } from "ag-grid-community";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { WorkOrderAttachment } from "../../../models/WorkOrderAttachment";

export const useWorkOrderAttachmentGridActionsAndButtons = ({
	gridRef,
	searchWorkOrderAttachment,
	setShowLinkResourcesToAssetDialog,
	setRemoveAttachmentError,
	setListMessage,
	WorkOrderId,
}: {
	WorkOrderId?: number | null;
	newMode?: boolean;
	gridRef: GridApi | null;
	searchWorkOrderAttachment: () => void;
	setRemoveAttachmentError: (err: string) => void | null;
	setListMessage: (err: string) => void | null;
	setShowLinkResourcesToAssetDialog?: (show: boolean) => void;
}) => {
	const {
		state: { workOrderAttachmentsList, listAttachmentSelected },
		actions: {
			setFirstTime,
			setWorkOrderAttachmentsList,
			setDataAttachmentSelected,
			setWorkOrderAttachmentToAdd,
			setListAttachmentSelected,
		},
	} = useWorkOrderModalsContext();

	const [refreshTrigger, setRefreshTrigger] = useState(1);

	// BUTTONS STATES
	const [addButton, setAddButton] = useState<boolean>(true);
	const [deleteButton, setDeleteButton] = useState<boolean>(false);
	const [saveButton, setSaveButton] = useState<boolean>(false);
	const [discardButton, setDiscardButton] = useState<boolean>(false);
	const [deleteOpen, setDeleteOpen] = useState(false);
	const isMountRef = useRef(true);
	const [searchParams] = useSearchParams();

	const refresh = useCallback(() => {
		if (!isMountRef.current) {
			const updatedList = workOrderAttachmentsList.filter(
				(item: WorkOrderAttachment & { id?: number }) => item.id !== -1,
			);

			setWorkOrderAttachmentsList(updatedList);
			setFirstTime(true);
			setSaveButton(false);
			setDiscardButton(false);
			setAddButton(true);
			setDeleteButton(false);
			setDeleteOpen(false);
			setDataAttachmentSelected(undefined);
			searchWorkOrderAttachment();
		}
	}, [searchParams, WorkOrderId]);

	const saveRow = useCallback(async () => {
		if (!listAttachmentSelected || !listAttachmentSelected[0]?.UploadFileId) {
			setListMessage("Attachment not selected");
		} else {
			const newWorkOrderAttachment = {
				UploadFileId: listAttachmentSelected[0].UploadFileId,
				FileName: listAttachmentSelected[0].FileName,
				IsAssetResource: false,
			};
			setWorkOrderAttachmentToAdd(newWorkOrderAttachment);
			const res = await linkWorkOrderAttachments(WorkOrderId as number, [
				newWorkOrderAttachment,
			]);
			if (res.ok) {
				if (setShowLinkResourcesToAssetDialog) {
					setShowLinkResourcesToAssetDialog(true);
				}
				setRefreshTrigger((prev) => prev + 1);
			} else {
				setListMessage(res.message);
			}
		}

		setListAttachmentSelected(null);
		setAddButton(true);
		setSaveButton(false);
		setDiscardButton(false);
		setDeleteButton(false);
	}, [
		listAttachmentSelected,
		WorkOrderId,
		linkWorkOrderAttachments,
		setWorkOrderAttachmentToAdd,
		setShowLinkResourcesToAssetDialog,
		setRefreshTrigger,
		setListMessage,
		setListAttachmentSelected,
		setAddButton,
		setSaveButton,
		setDiscardButton,
		setDeleteButton,
	]);

	const removeAttachment = useCallback(
		async (attachmentsToRemove: WorkOrderAttachment[]) => {
			if (attachmentsToRemove && attachmentsToRemove.length > 0) {
				const formattedAttachments = attachmentsToRemove.map(
					(attachment: WorkOrderAttachment) => ({
						UploadFileId: attachment.UploadFileId,
						IsAssetResource: attachment.IsAssetResource,
					}),
				);

				const res = await unLinkWorkOrderAttachments(
					WorkOrderId as number,
					formattedAttachments,
				);
				if (res.ok) {
					refresh();
				} else {
					setRemoveAttachmentError(res.message);
				}
			}

			setDataAttachmentSelected(null);
			setDeleteOpen(false);
			setAddButton(true);
			setSaveButton(false);
			setDiscardButton(false);
			setDeleteButton(false);
			setListAttachmentSelected([]);

			if (gridRef) {
				gridRef.deselectAll();
			}
		},
		[
			WorkOrderId,
			unLinkWorkOrderAttachments,
			refresh,
			setRemoveAttachmentError,
			setDataAttachmentSelected,
			setDeleteOpen,
			setAddButton,
			setSaveButton,
			setDiscardButton,
			setDeleteButton,
			setListAttachmentSelected,
			gridRef,
		],
	);

	useEffect(() => {
		refresh();
	}, [refreshTrigger]);

	useEffect(() => {
		isMountRef.current = false;
	}, []);

	return {
		refresh,
		saveRow,
		removeAttachment,
		deleteButton,
		saveButton,
		discardButton,
		deleteOpen,
		setSaveButton,
		setDiscardButton,
		setAddButton,
		setDeleteButton,
		setDeleteOpen,
	};
};
